import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getShortcutText } from '../../helpers/hotkeys';
import { getOrCreateOrigin, positionOriginAtElement } from '../../helpers/uiHelpers';
import Icon from '../Icon';

export default function ContextMenu({ items = [], triggerRef, onClose, level = 0, placement = 'right', inline = false, menuClass = '', closeOnSelect = true }) {
  const [/* position removed — inline positioning not in use */, setPosition] = useState({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);
  const menuRef = useRef();

  if (typeof document !== 'undefined' && !containerRef.current) {
    containerRef.current = getOrCreateOrigin({ className: 'context-origin', preferSibling: '.tooltip-origin' });
  }

  useLayoutEffect(() => {
    if (!triggerRef?.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 180;
    const menuHeight = items.length * 36;
    const offset = 8; // spacing between trigger and menu

    let place = placement || 'right';
    try {
      const attr = triggerRef.current.getAttribute && (triggerRef.current.getAttribute('data-context-placement') || triggerRef.current.getAttribute('data-tooltip-placement'));
      if (attr) place = attr.trim();
    } catch (err) {}
    place = place.toString().toLowerCase();

    const parts = place.split(/\s+/);
    const horizontal = parts.find(p => ['left', 'right'].includes(p));
    const vertical = parts.find(p => ['top', 'above', 'bottom', 'below'].includes(p));
    const primaryDirection = horizontal || vertical || parts[0] || 'right';

    let left = rect.right;
    let top = rect.top;

    if (horizontal && vertical) {
      if (horizontal === 'right') {
        left = rect.right + offset;
      } else if (horizontal === 'left') {
        left = rect.left - menuWidth - offset;
      }
      if (vertical === 'top' || vertical === 'above') {
        top = rect.top;
      } else if (vertical === 'bottom' || vertical === 'below') {
        top = rect.bottom - menuHeight;
      }
    }
    else if (primaryDirection === 'above' || primaryDirection === 'top') {
      left = rect.left + rect.width / 2 - menuWidth / 2;
      top = rect.top - menuHeight - offset;
      try {
        const toolbar = typeof document !== 'undefined' 
          ? (document.getElementById('toolbar') || 
             document.getElementById('toolbarLeft') || 
             document.getElementById('toolbarRight'))
          : null;
        if (toolbar && toolbar.contains(triggerRef.current)) {
          const tbRect = toolbar.getBoundingClientRect();
          top = tbRect.top - menuHeight - offset;
        }
      } catch (err) {}
    } else if (primaryDirection === 'bottom' || primaryDirection === 'below') {
      left = rect.left + rect.width / 2 - menuWidth / 2;
      top = rect.bottom + offset;
    } else if (primaryDirection === 'left') {
      left = rect.left - menuWidth - offset;
      top = rect.top + rect.height / 2 - menuHeight / 2;
    } else {
      left = rect.right + offset;
      top = rect.top + rect.height / 2 - menuHeight / 2;
    }

    if (left + menuWidth > window.innerWidth) left = Math.max(8, window.innerWidth - menuWidth - 8);
    if (left < 8) left = 8;
    if (top + menuHeight > window.innerHeight) top = Math.max(8, window.innerHeight - menuHeight - 8);
    if (top < 8) top = 8;

    const useOrigin = (horizontal || vertical || ['top', 'bottom', 'left', 'right', 'above', 'below'].includes(primaryDirection)) && !inline;
    if (useOrigin) {
      const applyWrapper = () => positionOriginAtElement(containerRef.current, triggerRef.current);

      applyWrapper();

      const resizeHandler = () => applyWrapper();
      window.addEventListener('resize', resizeHandler);
      window.addEventListener('scroll', resizeHandler, true);

      try {
        if (typeof window !== 'undefined') {
          const prev = window.__currentContextMenuClose;
          if (prev && prev !== onClose) prev();
          window.__currentContextMenuClose = onClose;
        }
      } catch (err) {}
      setReady(true);

      return () => {
        try {
          window.removeEventListener('resize', resizeHandler);
          window.removeEventListener('scroll', resizeHandler, true);
        } catch (err) {}
      };
    } else {
      if (inline && triggerRef.current && triggerRef.current.offsetParent) {
        const parentRect = triggerRef.current.offsetParent.getBoundingClientRect();
        setPosition({ top: top - parentRect.top, left: left - parentRect.left });
      } else {
        setPosition({ top: top + window.scrollY, left: left + window.scrollX });
      }
    }

    try {
      if (typeof window !== 'undefined') {
        const prev = window.__currentContextMenuClose;
        if (prev && prev !== onClose) prev();
        window.__currentContextMenuClose = onClose;
      }
    } catch (err) {}

    setReady(true);
  }, [triggerRef, items.length, placement, inline, onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(e.target)) return;
      if (triggerRef?.current && triggerRef.current.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [triggerRef, onClose]);

  useEffect(() => {
    return () => {
      try {
        if (typeof window !== 'undefined' && window.__currentContextMenuClose === onClose) window.__currentContextMenuClose = null;
      } catch (err) {}
    };
  }, [onClose]);

  const handleClick = (item, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (item.confirm) {
      if (window.confirm(item.confirm)) {
        item.onClick?.(e);
        if (closeOnSelect) onClose?.();
      }
    } else {
      item.onClick?.(e);
      if (closeOnSelect) onClose?.();
    }
  };

  const hasShortcuts = items.some(it => it && getShortcutText(it.label));
  const rawPlacementClass = (((triggerRef?.current?.getAttribute && (triggerRef.current.getAttribute('data-context-placement') || triggerRef.current.getAttribute('data-tooltip-placement'))) || placement || '')).toString().toLowerCase();
  const placementClass = rawPlacementClass
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => {
      if (token === 'above') return 'top';
      if (token === 'below') return 'bottom';
      return token;
    })
    .join(' ');

  const innerMenu = (
    <div
      className={`contextMenu ${inline ? 'inline' : ''} ${placementClass} ${menuClass} ${hasShortcuts ? 'has-shortcuts' : ''} ${ready ? 'show' : ''}`}
      ref={menuRef}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <ul>
        {items.map(({ label, description, iconClass, onClick, confirm, children, isActive, divider, className, isDisabled }, idx) => {
          if (divider) return <li key={idx}><div className="contextDivider" /></li>;
          const shortcutText = getShortcutText(label);
          const hasDescription = Boolean(String(description || '').trim());
          const iconTokens = String(iconClass || '').replace(/^ph[- ]?/, '').trim().split(/\s+/).filter(Boolean);
          const iconName = iconTokens[0] || '';
          const iconExtraClasses = iconTokens.slice(1).join(' ');
          return (
            <li key={idx}>
              <div
                className={`contextItem ${hasDescription ? 'hasDescription' : ''} ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''} ${className || ''}`}
                aria-disabled={isDisabled ? 'true' : 'false'}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { if (!children && !isDisabled) handleClick({ label, iconClass, onClick, confirm }, e); }}
              >
                {iconName && (
                  <Icon icon={iconName} className={`icon ${iconName} ${iconExtraClasses}`.trim()} />
                )}
                <span className="contextText">
                  <span className="contextLabel">{label}</span>
                  {hasDescription && <span className="contextSubline">{description}</span>}
                </span>
                {shortcutText && <span className="contextShortcut">{shortcutText}</span>}
                {children && (
                  <Icon icon={'caret-right'} className="icon nestedIndicator" />
                )}
              </div>
              {children && (
                <ContextMenu
                  items={children}
                  triggerRef={menuRef}
                  onClose={onClose}
                  level={level + 1}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  if (!inline && typeof document !== 'undefined') {
    if (containerRef.current) return createPortal(innerMenu, containerRef.current);
    return createPortal(innerMenu, document.body);
  }

  return innerMenu;
}
